import { ApiResponse, BaseBodyResponse } from "api/api.d";
import { api } from "api/api";
import { NotiStickyResponse } from "dto/notifications/response/NotificationResponse.d";
import { NsType } from "../dto/notifications/response/enum";

/**
 * NS_001  getNotificationSticky  uses the standard REST envelope
 * NS_003  markAsRead              uses a direct URL path
 * NS_004  markAllAsRead           uses the standard REST envelope
 *
 * There is no longer a separate getNsUnView (NS_002) API.
 * The unread count is returned as `notifyUnview` inside the getNotificationSticky response.
 */

const NOTI_REST = "/notification-read-service/rest/web/request";
const NOTI_DIRECT = "/notification-read-service/biz/web/notifications";

// ─── Request types ─────────────────────────────────────────────────────────

export interface GetNotificationStickyRequest {
  nsType?: NsType;
  fromDate?: string;   // yyyyMMdd
  toDate?: string;     // yyyyMMdd
  pageNo?: number;
  pageSize?: number;
  status?: string;     // "Y" | "N"
  isPriority?: string;
  type?: string;       // business category filter: "ACCOUNT" | "CARD" | "TRANSACTION" | "SECURITY"
}

export interface MarkAsReadRequest {
  id: string;
  nsType: string; // "U" | "S" | "N"
}

export interface MarkAllAsReadRequest {
  nsType?: string; // "P" | "N" — default "P"
}

// ─── API ────────────────────────────────────────────────────────────────────

export const notificationAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    /** NS_001 — getNotificationSticky (REST envelope) */
    getNotificationSticky: builder.query<
      ApiResponse<NotiStickyResponse>,
      GetNotificationStickyRequest | void
    >({
      query: (args) => {
        const {
          nsType = NsType.Private,
          fromDate = "",
          toDate = "",
          pageNo = 1,
          pageSize = 20,
          status,
          isPriority,
          type,
        } = args ?? {};
        return {
          url: NOTI_REST,
          method: "POST",
          functionName: "getNotificationSticky",
          categoryCode: "NOTIFICATION_HOME",
          data: {
            nsType,
            fromDate,
            toDate,
            pageNo,
            pageSize,
            ...(status !== undefined && { status }),
            ...(isPriority !== undefined && { isPriority }),
            ...(type !== undefined && type !== "ALL" && { type }),
          },
        };
      },
      providesTags: () => [{ type: "getNotificationSticky", id: "LIST" }],
    }),

    /** NS_003 — markAsRead (REST envelope, same base as getNotificationSticky) */
    markAsRead: builder.mutation<
      ApiResponse<BaseBodyResponse>,
      MarkAsReadRequest
    >({
      query: (data) => ({
        url: NOTI_REST,
        method: "POST",
        functionName: "markAsRead",
        categoryCode: "NOTIFICATION_HOME",
        data,
      }),
      invalidatesTags: [{ type: "getNotificationSticky", id: "LIST" }],
    }),

    /** NS_004 — markAllAsRead (REST envelope) */
    markAllAsRead: builder.mutation<
      ApiResponse<BaseBodyResponse>,
      MarkAllAsReadRequest | void
    >({
      query: (args) => ({
        url: NOTI_REST,
        method: "POST",
        functionName: "markAllAsRead",
        categoryCode: "NOTIFICATION_HOME",
        data: { nsType: args?.nsType ?? NsType.Private },
      }),
      invalidatesTags: [{ type: "getNotificationSticky", id: "LIST" }],
    }),
  }),
});

export const {
  useGetNotificationStickyQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
} = notificationAPI;
